import React from 'react';
import { authFetch } from '../authFetch';
import { getOfficialSkillThumbnailUrl } from '../client';

interface UseOfficialSkillThumbnailOptions {
  skillId: string | null;
  /** 썸네일 존재 여부 (없으면 요청하지 않음). */
  hasThumbnail?: boolean;
  /** 캐시 무효화용 버전. 업로드 시 증가. */
  version?: number;
  /** 뷰포트 진입 등으로 실제 요청을 켤지 여부. */
  enabled?: boolean;
}

interface ThumbnailCacheEntry {
  objectUrl: string | null;
  promise: Promise<string | null> | null;
  refCount: number;
  cleanupTimer: number | null;
}

const CACHE_TTL_MS = 30_000;
const cache = new Map<string, ThumbnailCacheEntry>();

function buildCacheKey(skillId: string, version: number): string {
  return `official-skill:${skillId}:${version}`;
}

function scheduleCleanup(cacheKey: string) {
  const entry = cache.get(cacheKey);
  if (!entry) return;
  if (entry.cleanupTimer !== null) {
    window.clearTimeout(entry.cleanupTimer);
  }
  entry.cleanupTimer = window.setTimeout(() => {
    const latest = cache.get(cacheKey);
    if (!latest || latest.refCount > 0) return;
    if (latest.objectUrl) {
      URL.revokeObjectURL(latest.objectUrl);
    }
    cache.delete(cacheKey);
  }, CACHE_TTL_MS);
}

async function retain(cacheKey: string, url: string): Promise<string | null> {
  let entry = cache.get(cacheKey);
  if (!entry) {
    entry = { objectUrl: null, promise: null, refCount: 0, cleanupTimer: null };
    cache.set(cacheKey, entry);
  }

  entry.refCount += 1;
  if (entry.cleanupTimer !== null) {
    window.clearTimeout(entry.cleanupTimer);
    entry.cleanupTimer = null;
  }

  if (entry.objectUrl) {
    return entry.objectUrl;
  }

  if (!entry.promise) {
    entry.promise = (async () => {
      const res = await authFetch(url, { headers: { Accept: 'image/webp,image/*' } });
      if (!res.ok) {
        throw new Error(`thumbnail ${res.status}`);
      }
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const latest = cache.get(cacheKey);
      if (!latest) {
        URL.revokeObjectURL(objectUrl);
        return null;
      }
      latest.objectUrl = objectUrl;
      latest.promise = null;
      return objectUrl;
    })().catch(() => {
      const latest = cache.get(cacheKey);
      if (latest) {
        latest.promise = null;
      }
      return null;
    });
  }

  return entry.promise;
}

function release(cacheKey: string) {
  const entry = cache.get(cacheKey);
  if (!entry) return;
  entry.refCount = Math.max(0, entry.refCount - 1);
  if (entry.refCount === 0) {
    scheduleCleanup(cacheKey);
  }
}

/**
 * 공식 스킬 썸네일(WebP)을 authFetch로 받아 object URL을 돌려준다.
 * `<img src>`는 Bearer 헤더를 못 보내므로 blob → createObjectURL 경로가 필요하다.
 */
export function useOfficialSkillThumbnail({
  skillId,
  hasThumbnail = false,
  version = 0,
  enabled = true,
}: UseOfficialSkillThumbnailOptions): string | null {
  const [objectUrl, setObjectUrl] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!enabled || !skillId || !hasThumbnail) {
      setObjectUrl(null);
      return;
    }

    let disposed = false;
    const cacheKey = buildCacheKey(skillId, version);
    setObjectUrl(null);

    void (async () => {
      const resolved = await retain(cacheKey, getOfficialSkillThumbnailUrl(skillId, version));
      if (disposed) return;
      setObjectUrl(resolved);
    })();

    return () => {
      disposed = true;
      release(cacheKey);
    };
  }, [enabled, skillId, hasThumbnail, version]);

  return objectUrl;
}
